import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

/** A labelled text field that saves on blur, only when the value actually changed. */
export function AutoField({
  label,
  value,
  onSave,
  rows = 0,
  placeholder,
  hint
}: {
  label: string;
  value: string;
  onSave: (next: string) => void | Promise<void>;
  rows?: number;
  placeholder?: string;
  hint?: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => {
    if (text !== value) void onSave(text);
  };
  return (
    <div className="field">
      <label className="label">{label}</label>
      {rows > 0 ? (
        <textarea className="textarea" rows={rows} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onBlur={commit} />
      ) : (
        <input className="input" value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onBlur={commit} />
      )}
      {hint && <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

/** Run an async action with a busy flag and a toast on failure. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const run = async <T,>(key: string, fn: () => Promise<T>, success?: string): Promise<T | undefined> => {
    setBusy(key);
    try {
      const result = await fn();
      if (success) toast.success(success);
      return result;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
      return undefined;
    } finally {
      setBusy(null);
    }
  };
  return { busy, run };
}

/** A primary-ish AI action button with a spinner while it runs. */
export function AiButton({
  busy,
  onClick,
  children,
  disabled,
  kind = "default",
  title
}: {
  busy: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  kind?: "default" | "primary" | "ghost";
  title?: string;
}) {
  return (
    <button className={`btn btn-sm ${kind === "primary" ? "btn-primary" : kind === "ghost" ? "btn-ghost" : ""}`} onClick={onClick} disabled={busy || disabled} title={title}>
      <Icon name={busy ? "clock" : "sparkles"} size={14} className={busy ? "spin" : ""} /> {children}
    </button>
  );
}

export function SectionCard({ icon, title, actions, children, className }: { icon: Parameters<typeof Icon>[0]["name"]; title: string; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card pad ${className ?? ""}`}>
      <div className="row gap-2" style={{ marginBottom: 14 }}>
        <Icon name={icon} size={17} style={{ color: "var(--accent)" }} />
        <h3>{title}</h3>
        <div className="spacer" />
        {actions}
      </div>
      {children}
    </section>
  );
}
